import { APP_MODULES } from 'constants/appModules';
import { PATHS } from 'constants/paths';
import { getLastActiveWellbore, getWellChoice, OptionType } from 'helpers/WellHelper';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import { Option } from 'react-select/src/filters';
import { updateCurrentWellAndWellbore } from 'redux/Wells';
import { useSelectAuthorizedWellbores, useSelectCurrentWell } from 'redux/Wells/selectors';
import { WellFullDetailsWithUniqueKeyDTO } from 'types/well';

type ValueType<OptionType> = OptionType | null | undefined;
const MINIMUM_NUMBER_OF_CHARACTERS_IN_SEARCH = 2;

export interface Props {
  currentModule?: APP_MODULES | null;
  wellboresLoading: boolean;
  wellOptions: Array<{
    label: string;
    options: OptionType[];
  }>;
  wells: WellFullDetailsWithUniqueKeyDTO[];
  path: string;
  tightWellAccessible?: boolean;
  isFavouriteVisible?: boolean;
  propMenuStyle?: string;
  dropdownPropStyle?: string;
  closePopUp?: Function;
}

export const useWellSelect = ({
  currentModule,
  wellboresLoading,
  wellOptions,
  wells,
  path,
  tightWellAccessible,
  isFavouriteVisible = true,
  propMenuStyle,
  dropdownPropStyle,
  closePopUp,
}: Props) => {
  const history = useHistory();
  const well = useSelectCurrentWell();
  const authWellBore = useSelectAuthorizedWellbores();
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [favouritePopinIsOpen, setFavouritePopinIsOpen] = useState<boolean>(false);
  const [selectedWell, setSelectedWell] = useState(well ? getWellChoice(well) : null);
  const dispatch = useDispatch();

  const filterOptions = (candidate: Option, input: string) => {
    if (
      candidate.data.isActive === false &&
      input.length < MINIMUM_NUMBER_OF_CHARACTERS_IN_SEARCH
    ) {
      return false;
    }

    return candidate.label.toLowerCase().includes(input.toLowerCase().trim());
  };

  useEffect(() => {
    setSelectedWell(well ? getWellChoice(well) : null);
  }, [well]);

  const redirectToNextPage = useCallback(
    (nextWellIdAndTuwi: any) => {
      const { tuwi, wellId } = JSON.parse(nextWellIdAndTuwi);
      const nextWell = wells.find(
        well => (wellId && well.wellId === wellId) || (tuwi && well.tuwi === tuwi),
      );

      const nextWellbore = getLastActiveWellbore(nextWell);
      const nextWellTuwi = nextWell?.tuwi;
      // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
      setSelectedWell(getWellChoice(nextWell!));

      const redirectToHome = (nextWell: any) => {
        const nextWellbore = getLastActiveWellbore(nextWell);
        dispatch(
          updateCurrentWellAndWellbore({
            well: nextWell,
            wellbore: {
              ...nextWellbore,
              well: nextWell,
            },
          }),
        );
        if (nextWell && nextWell.tightWell && !tightWellAccessible) {
          history.push(
            generatePath(PATHS.HOME, {
              wellboreId: nextWell.wellId,
            }),
          );
        } else if (nextWellTuwi) {
          history.push(
            generatePath(PATHS.EDDR_OVERVIEW, {
              tuwi: nextWellTuwi,
            }),
          );
        } else {
          // Fallback to HOME if no tuwi available
          history.push(
            generatePath(PATHS.HOME, {
              wellboreId: nextWell.wellId,
            }),
          );
        }
      };

      if (path === PATHS.LESSONS_LEARNT_WELL) {
        if (nextWell) {
          dispatch(updateCurrentWellAndWellbore({ well: nextWell }));
        }
        history.push(generatePath(PATHS.LESSONS_LEARNT_WELL, {}));
      } else if (
        path === PATHS.EDDR_OVERVIEW ||
        path === PATHS.EDDR_SAFETY ||
        path === PATHS.EDDR_BHA ||
        path === PATHS.EDDR_FLUIDS ||
        path === PATHS.EDDR_WELL_ACTIVITY_HISTORY ||
        path === PATHS.EDDR_OPERATIONS_LOG ||
        path === PATHS.EDDR_TRAJECTORY
      ) {
        if (nextWell) {
          dispatch(updateCurrentWellAndWellbore({ well: nextWell }));
        }

        if (nextWellTuwi) {
          if (nextWell && nextWell.tightWell && !tightWellAccessible) {
            const nextWellbore = getLastActiveWellbore(nextWell);
            dispatch(
              updateCurrentWellAndWellbore({
                well: nextWell,
                wellbore: {
                  ...nextWellbore,
                  well: nextWell,
                },
              }),
            );
            history.push(
              generatePath(PATHS.HOME, {
                wellboreId: nextWell.wellId,
              }),
            );
          } else {
            history.push(generatePath(path, { tuwi: nextWellTuwi }));
          }
        }

        if (!tuwi && nextWellTuwi) {
          history.push(
            generatePath(PATHS.EDDR_OVERVIEW, {
              tuwi: nextWellTuwi,
            }),
          );
        }
      } else if (path === PATHS.LESSONS_LEARNT_REGISTER) {
        if (nextWell) {
          dispatch(updateCurrentWellAndWellbore({ well: nextWell }));
        }
        history.push(generatePath(PATHS.LESSONS_LEARNT_REGISTER, {}));
      } else if (nextWellTuwi) {
        // Default fallback: redirect to EDDR Overview
        redirectToHome(nextWell);
      } else {
        // Ultimate fallback: redirect to EDDR_OVERVIEW with tuwi if available
        if (nextWellTuwi) {
          history.push(generatePath(PATHS.EDDR_OVERVIEW, { tuwi: nextWellTuwi }));
        }
      }
    },
    [wells, path, history, dispatch, authWellBore, tightWellAccessible, well?.wellId],
  );

  const onWellboreChange = (selectedOption: ValueType<OptionType>) => {
    closePopUp && closePopUp();
    const { value } = selectedOption as OptionType;
    redirectToNextPage(value);
  };

  const handleManageYourFavouriteOnClick = () => setFavouritePopinIsOpen(true);

  const handleFavouritePopinOnClose = useCallback(() => setFavouritePopinIsOpen(false), []);

  return {
    snackbarOpen,
    setSnackbarOpen,
    selectedWell,
    onWellboreChange,
    favouritePopinIsOpen,
    handleFavouritePopinOnClose,
    filterOptions,
    redirectToNextPage,
    handleManageYourFavouriteOnClick,
  };
};
